import { ArrowRight, Volume2 } from "lucide-react";
import { useState, useEffect, useContext } from "react";
import Header from "../Header";
import { LanguageContext } from "../../utils/context/languageContext";
import { LanguageKeys } from "../../utils/languageKeys";
import SpecificButton from "../../atoms/specificButton/SpecificButton";
import { useConfig } from "../../utils/context/configContext";

interface VolumeStepProps {
  onNext: () => void;
}

export default function VolumeScreen({ onNext }: VolumeStepProps) {
  const { translate } = useContext(LanguageContext) || {};
  const config = useConfig();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: any) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const toggleAnimationPause = () => {
    setIsAnimationPaused((prev) => !prev);
  };

  function hexToRgba(hex: string, alpha: number = 1): string {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex.split("").map((h) => h + h).join("");
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const brandColor = config?.style?.base?.brandColor || "#000";
  const rgba1 = hexToRgba(brandColor, 0.19);
  const rgba2 = hexToRgba(brandColor, 0.23);
  const rgba3 = hexToRgba(brandColor, 0.24);

  const calculateHolographicEffect = () => {
    const windowWidth = typeof window !== "undefined" ? window.innerWidth : 1000;
    const windowHeight = typeof window !== "undefined" ? window.innerHeight : 1000;
    const normalizedX = (mousePosition.x / windowWidth) * 2 - 1;
    const normalizedY = (mousePosition.y / windowHeight) * 2 - 1;
    const rotateX = normalizedY * 5;
    const rotateY = normalizedX * -5;
    return {
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
      background: "#fff",
      boxShadow: `0 0 20px ${rgba1}, 0 0 30px ${rgba2}`,
    };
  };

  const holographicStyle = calculateHolographicEffect();

  return (
    <div
      data-testid="educational-root"
      data-educational-step="2"
      className="flex h-full max-w-[28rem] mx-auto w-full flex-col relative items-center justify-center  overflow-hidden max-w-md mx-auto bg-primary text-base"
      style={{ background: config?.style?.base?.backgroundColor }}
    >
      <div className="flex justify-start absolute z-[99] top-[1rem] max-w-md mx-auto w-full px-[1rem]">
        <Header noTitle />
      </div>
      <div className="relative mb-[2rem]">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="absolute  rounded-[9999px]"
            style={{
              width: "100px",
              height: "100px",
              minHeight: "100px",
              minWidth: "100px",
              background: config?.style?.base?.backgroundColor || "#fff",
              boxShadow: `0 0 20px 2px ${rgba3}`,
              border: "none",
              filter: "blur(1px)",
              margin: "0 auto",
              left: "50%",
              top: "50%",
              animation: isAnimationPaused
                ? "none"
                : `gentleRipple 15s cubic-bezier(0.1, 0.5, 0.2, 1) ${i * 5}s infinite`,
              opacity: isAnimationPaused ? 1 : 0,
              transform: isAnimationPaused
                ? "translate(-50%, -50%) scale(0.5)"
                : "translate(-50%, -50%)",
            }}
          />
        ))}
        <div
          className="relative z-10 rounded-[9999px] p-[1.5rem] cursor-pointer transition-all backdrop-blur-sm"
          style={{
            ...holographicStyle,
            transition: "transform 0.1s ease-out, box-shadow 0.1s ease-out",
          }}
          onClick={toggleAnimationPause}
        >
          <Volume2
            className="w-[3rem] h-[3rem]  relative z-10"
            style={{ filter: "drop-shadow(0 0 5px rgba(255, 255, 255, 0.7))" }}
            color={config?.style?.base?.primaryColor || "#000"}
          />
        </div>
      </div>

      <div className="relative z-10  text-center max-w-[20rem]">
        <div
          className=" mb-[.5rem]"
          style={{
            fontFamily: config?.style?.subheading?.subheadingFontFamily || "'Inter', sans-serif",
            fontSize: config?.style?.subheading?.subheadingFontSize || "14px",
            color: config?.style?.subheading?.subheadingColor || "#4b5563",
            fontWeight: config?.style?.subheading?.subheadingFontWeight || "normal",
          }}
        >
          {translate?.(LanguageKeys.turnUpVolume)}
        </div>
      </div>

      <div className="absolute bottom-[1rem] right-0 max-w-[28rem] mx-auto w-full px-[1rem] max-w-md mx-auto">
        <div className="flex justify-end">
          <SpecificButton
            buttonFunc={() => onNext()}
            data-testid="educational-next-btn"
            postfixIcon={<ArrowRight />}
            buttonText={translate?.(LanguageKeys.continue)}
          />
        </div>
      </div>
      <style>{`
  @keyframes gentleRipple {
    0% {
      transform: translate(-50%, -50%) scale(0.5) translateZ(0);
      opacity: 0.7;
    }
    50% {
      opacity: 0.4;
    }
    100% {
      transform: translate(-50%, -50%) scale(25) translateZ(0);
      opacity: 0;
    }
  }
`}</style>
    </div>
  );
}
